import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import ReviewTile from "@/components/ReviewTile";
import reviewsData from "@/data/reviews.json";

const STRIP_MOTION = {
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};
const TILE_MOTION = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

/**
 * Pick reviews matching the given industry/product/location tag, ranked by
 * (rating desc, text length desc — longer reviews read as more genuine).
 * Falls back to the global top-rated set when the filtered list is sparser
 * than `count`. Location is hidden from public tiles but drives local-SEO
 * landing pages (see LocationPage).
 */
function pickReviews({ industry, product, location, count, reviews }) {
  const scored = reviews.map((r) => ({
    r,
    matches:
      (industry && Array.isArray(r.industries) && r.industries.includes(industry)) ||
      (product && Array.isArray(r.products) && r.products.includes(product)) ||
      (location && Array.isArray(r.locations) && r.locations.includes(location)),
  }));

  const tagged = scored
    .filter((s) => s.matches)
    .map((s) => s.r)
    .sort(
      (a, b) =>
        (b.rating || 0) - (a.rating || 0) || (b.text?.length || 0) - (a.text?.length || 0)
    );

  if (tagged.length >= count) return { reviews: tagged.slice(0, count), mode: "tagged" };

  // Fallback — top-rated globally, excluding any we've already picked.
  const already = new Set(tagged.map((r) => r.text));
  const topRated = [...reviews]
    .filter((r) => !already.has(r.text))
    .sort(
      (a, b) =>
        (b.rating || 0) - (a.rating || 0) || (b.text?.length || 0) - (a.text?.length || 0)
    )
    .slice(0, count - tagged.length);

  return { reviews: [...tagged, ...topRated], mode: tagged.length === 0 ? "fallback" : "mixed" };
}

/**
 * Dynamic "what customers say" strip for product + industry + partner + location
 * pages. Uses the shared <ReviewTile /> so design stays identical to /reviews.
 *
 * @param {string} [industry]
 * @param {string} [product]
 * @param {string} [location]  — city slug (liverpool, manchester…); admin-only
 *                                taxonomy, never rendered as a visible chip.
 * @param {string} [eyebrow]
 * @param {string} [heading]
 * @param {string} [subheading]
 * @param {number} [count]
 * @param {string} [bg]
 * @param {string} [testId]
 */
export default function RelatedReviews({
  industry,
  product,
  location,
  eyebrow = "Customer Stories",
  heading,
  subheading,
  count = 3,
  bg = "bg-gray-50",
  testId = "related-reviews",
}) {
  const { reviews: baseReviews, totals } = reviewsData;

  const { reviews, mode } = useMemo(
    () => pickReviews({ industry, product, location, count, reviews: baseReviews }),
    [industry, product, location, count, baseReviews]
  );

  if (reviews.length === 0) return null;

  const finalHeading =
    heading || (industry ? `See what our ${industry} customers say` : "What our customers say");

  return (
    <section className={`py-14 md:py-20 ${bg}`} data-testid={testId} data-mode={mode}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          {eyebrow && (
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">
              {eyebrow}
            </p>
          )}
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-4">
            {finalHeading}
          </h2>
          {subheading && (
            <p className="text-brand-grey max-w-2xl mx-auto">{subheading}</p>
          )}
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={STRIP_MOTION}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch"
        >
          {reviews.map((r) => (
            <ReviewTile
              key={`${r.source}::${r.author}::${(r.text || "").slice(0, 40)}`}
              review={r}
              motionVariant={TILE_MOTION}
              testId={`related-review-${r.source}`}
            />
          ))}
        </motion.div>

        <div className="mt-8 flex justify-center">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-aqua-dark hover:text-brand-black transition-colors group"
            data-testid={`${testId}-see-all`}
          >
            {`See all ${totals.total} verified reviews`}
            <ArrowRight
              size={14}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
